import { useMemo, useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import Layout from "../Layout";
import SchemaFormRender from "@/components/Schema/SchemaFormRender";
import { removeMetaFields } from "@/lib/removeMetaFields";
import { DynamicModuleService, SchemaService } from "@/services/APIs";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createForm } from "@formily/core";
import { notify } from "@/components/common/Notification";
import { toast } from "sonner";

// Fields that are allowed to be edited
const EDITABLE_FIELDS = ["propertyname", "sqfootage"];

// Check if a property is a layout component (Card, FormTab, FormLayout, etc.)
const isLayoutComponent = (property: any): boolean => {
  if (!property || typeof property !== "object") {
    return false;
  }

  // Layout components are typically void type with x-component
  const isVoid = property.type === "void";
  const hasXComponent =
    property["x-component"] && typeof property["x-component"] === "string";

  // Common layout components
  const layoutComponents = [
    "Card",
    "FormTab",
    "FormLayout",
    "FormGrid",
    "Tabs",
    "FormItem",
  ];
  const componentName = property["x-component"];

  return isVoid && hasXComponent && layoutComponents.includes(componentName);
};

// Recursively modify schema to only show editable fields (hide all others)
const modifySchemaToRestrictFields = (schema: any): any => {
  if (!schema || typeof schema !== "object") {
    return schema;
  }

  // Handle schema with nested schema property
  if (schema.schema) {
    return {
      ...schema,
      schema: modifySchemaToRestrictFields(schema.schema),
    };
  }

  // If it's a property definition
  if (schema.properties) {
    const modifiedProperties: any = {};
    Object.keys(schema.properties).forEach(key => {
      const property = schema.properties[key];

      // Check if this is a layout component (Card, FormTab, etc.)
      if (isLayoutComponent(property)) {
        // Keep layout components and recursively process their properties
        const modifiedLayout = modifySchemaToRestrictFields(property);
        // Only include layout component if it has properties after filtering
        if (
          modifiedLayout?.properties &&
          Object.keys(modifiedLayout.properties).length > 0
        ) {
          modifiedProperties[key] = modifiedLayout;
        }
      } else {
        // For actual input fields, check if they're editable
        const isEditable = EDITABLE_FIELDS.some(
          field => field.toLowerCase() === key.toLowerCase()
        );

        // Only include fields that are in the editable list
        if (isEditable) {
          modifiedProperties[key] = modifySchemaToRestrictFields(property);
        }
        // Completely exclude non-editable fields
      }
    });

    // Return modified schema with filtered properties
    return {
      ...schema,
      properties: modifiedProperties,
    };
  }

  // If it's an array of items
  if (schema.items) {
    return {
      ...schema,
      items: modifySchemaToRestrictFields(schema.items),
    };
  }

  // Recursively process other object properties
  const modified: any = { ...schema };
  Object.keys(schema).forEach(key => {
    if (
      key !== "properties" &&
      key !== "items" &&
      key !== "schema" &&
      typeof schema[key] === "object"
    ) {
      modified[key] = modifySchemaToRestrictFields(schema[key]);
    }
  });
  return modified;
};

const PP_EditRehabEstimator: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams<{ id: string }>();

  const schemaName = "Property Master";
  const rehabEstimateSchemaName = "Rehab Estimate";

  const form = useMemo(() => createForm({}), []);

  // Rehab type to value mapping
  const rehabTypeValues: Record<string, string> = useMemo(
    () => ({
      EXCELLENT: "$20",
      GOOD: "$30",
      OK: "$40",
      ROUGH: "$50",
      "VACANT ROUGH": "$65",
      BAD: "$80",
    }),
    []
  );

  // Rehab type and value state
  const [rehabType, setRehabType] = useState<string>("");
  const [rehabValue, setRehabValue] = useState<string>("");

  // Rehab estimate data state
  const [rehabEstimateData, setRehabEstimateData] = useState<{
    rehabestimate: string | null;
  }>({
    rehabestimate: null,
  });

  // Store the rehab estimate record ID for updates
  const [rehabEstimateRecordId, setRehabEstimateRecordId] = useState<
    string | null
  >(null);

  // Flag to track if initial data has been loaded (to prevent overwriting during initial load)
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Update rehab value when rehab type changes
  useEffect(() => {
    if (rehabType && rehabTypeValues[rehabType]) {
      setRehabValue(rehabTypeValues[rehabType]);
    } else {
      setRehabValue("");
    }
  }, [rehabType, rehabTypeValues]);

  // Get schema columns first to know what fields to fetch
  const { data: rehabEstimateColumnData } = useQuery({
    queryKey: ["columnGridData", rehabEstimateSchemaName],
    queryFn: async () => {
      try {
        const result = await SchemaService.getColumnGridData(
          rehabEstimateSchemaName
        );
        return result ?? { grid: [] };
      } catch {
        return { grid: [] };
      }
    },
    enabled: !!rehabEstimateSchemaName,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    retry: false,
  });

  // Fetch Rehab Estimate data filtered by propertymaster_id
  const { data: rehabEstimateResponse, refetch: refetchRehabEstimate } =
    useQuery({
      queryKey: ["rehab-estimate", rehabEstimateSchemaName, id],
      queryFn: async () => {
        if (!rehabEstimateColumnData?.grid || !id) {
          return { results: [], count: 0 };
        }

        // Get all column names from schema
        const allColumns = rehabEstimateColumnData.grid.map(
          (field: any) => field.field_name
        );

        // Explicitly add "id" if not already present
        const lowerColumns = allColumns.map((col: string) => col.toLowerCase());
        if (!lowerColumns.includes("id")) {
          allColumns.push("id");
        }

        try {
          const response = await DynamicModuleService.getGridCoByConfig(
            rehabEstimateSchemaName,
            1,
            1000,
            allColumns,
            {
              propertymaster_id: id,
            }
          );

          console.log("Rehab Estimate response:", response);
          return response ?? { results: [], count: 0 };
        } catch (error) {
          console.error("Error fetching rehab estimate:", error);
          return { results: [], count: 0 };
        }
      },
      enabled:
        !!rehabEstimateSchemaName && !!rehabEstimateColumnData?.grid && !!id,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      retry: false,
    });

  // Update rehab estimate data when loaded
  useEffect(() => {
    if (rehabEstimateResponse) {
      const data =
        rehabEstimateResponse.results ??
        rehabEstimateResponse.data ??
        rehabEstimateResponse;

      let rehabEstimateArray: any[] = [];
      if (Array.isArray(data)) {
        rehabEstimateArray = data;
      } else if (data && Array.isArray(data.results)) {
        rehabEstimateArray = data.results;
      }

      // Get the first rehab estimate record for this property
      if (rehabEstimateArray.length > 0) {
        const firstRecord = rehabEstimateArray[0];
        // Normalize keys to lowercase for consistency
        const normalized: any = {};
        Object.keys(firstRecord).forEach(key => {
          normalized[key.toLowerCase()] = firstRecord[key];
        });

        // Store the record ID for updates
        const recordId = normalized.id ?? normalized._id ?? null;
        setRehabEstimateRecordId(recordId);

        const rehabData = {
          rehabestimate: normalized.rehabestimate ?? null,
        };
        console.log("Rehab Estimate data:", rehabData);
        setRehabEstimateData(rehabData);

        // Restore rehab type if it exists
        if (normalized.rehabtype) {
          setRehabType(normalized.rehabtype);
        }

        setIsInitialLoad(false); // Mark initial load as complete
      } else {
        // Reset to null if no data found
        setRehabEstimateRecordId(null);
        setRehabEstimateData({
          rehabestimate: null,
        });
        setIsInitialLoad(false); // Mark initial load as complete even if no data
      }
    } else {
      setRehabEstimateRecordId(null);
      setRehabEstimateData({
        rehabestimate: null,
      });
    }
  }, [rehabEstimateResponse]);

  // Function to save/update rehab estimate
  const saveRehabEstimate = useCallback(
    async (estimateValue: string | null) => {
      if (!id || !estimateValue) {
        return;
      }

      try {
        const saveData: any = {
          propertymaster_id: id,
          rehabestimate: estimateValue,
          rehabtype: rehabType ?? null, // Save rehab type along with estimate
        };

        if (rehabEstimateRecordId) {
          // Update existing record
          await DynamicModuleService.updateItem(
            rehabEstimateSchemaName,
            rehabEstimateRecordId,
            saveData
          );
          console.log("Rehab Estimate updated successfully");
        } else {
          // Create new record
          const response = await DynamicModuleService.createItem(
            rehabEstimateSchemaName,
            saveData
          );
          console.log("Rehab Estimate created successfully:", response);

          // Store the new record ID
          if (response?.id ?? response?.Id ?? response?.ID) {
            setRehabEstimateRecordId(response.id ?? response.Id ?? response.ID);
          }
        }

        // Refetch to ensure we have the latest data
        await refetchRehabEstimate();
      } catch (error: any) {
        console.error("Error saving rehab estimate:", error);
        const errorMessage =
          error?.response?.data?.message ??
          error?.message ??
          "Failed to save rehab estimate";
        notify.error(errorMessage);
      }
    },
    [
      id,
      rehabEstimateRecordId,
      rehabEstimateSchemaName,
      refetchRehabEstimate,
      rehabType,
    ]
  );

  // Calculate Rehab Estimate automatically: sqfootage * rehabvalue (but don't save automatically)
  useEffect(() => {
    // Don't calculate during initial load to preserve loaded value
    if (isInitialLoad) {
      return;
    }

    const sqfootage = form.values.sqfootage;
    const rehabValueStr = rehabValue;

    // Extract numeric value from rehabValue (e.g., "$20" -> 20)
    const numericRehabValue = rehabValueStr
      ? parseFloat(rehabValueStr.replace(/[^0-9.]/g, ""))
      : 0;

    // Only calculate if both values are available
    // Don't reset to null if values are missing - preserve existing loaded value
    if (sqfootage && numericRehabValue > 0) {
      const calculatedEstimate =
        (parseFloat(String(sqfootage)) ?? 0) * numericRehabValue;
      const estimateValue = calculatedEstimate.toString();

      // Only update if the calculated value is different from current value
      // This prevents unnecessary updates and preserves loaded values
      setRehabEstimateData(prev => {
        if (prev.rehabestimate !== estimateValue) {
          // Only update the display value, don't save automatically
          return {
            rehabestimate: estimateValue,
          };
        }
        return prev; // Keep existing value if it's the same
      });
    }
    // Removed the else block that was resetting to null
    // This preserves the loaded value when inputs are not available
  }, [form.values.sqfootage, rehabValue, isInitialLoad]);

  const { data: schemaJson } = useQuery({
    queryKey: ["schema", schemaName],
    queryFn: () => SchemaService.getSchemaByName(schemaName ?? ""),
    enabled: !!schemaName,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const { data: recordInfo } = useQuery({
    queryKey: ["dynamic-model", schemaName, id],
    queryFn: () => DynamicModuleService.getItemById(schemaName ?? "", id ?? ""),
    enabled: !!schemaName && !!id,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    retry: false,
  });

  // Modify schema to restrict fields
  const restrictedSchema = useMemo(() => {
    if (!schemaJson) {
      return null;
    }
    const schema = schemaJson.json ?? schemaJson;

    const modified = modifySchemaToRestrictFields(schema);
    return modified;
  }, [schemaJson]);

  // Set initial values when record is loaded
  useEffect(() => {
    if (recordInfo) {
      form.setInitialValues(removeMetaFields(recordInfo));
    }
  }, [recordInfo, form]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (!id) {
        notify.error("Property ID missing. Please reopen the page.");
        return;
      }

      // Save rehab estimate if we have a value
      if (rehabEstimateData.rehabestimate) {
        await saveRehabEstimate(rehabEstimateData.rehabestimate);
        toast.success("Rehab Estimate saved successfully!");
      } else {
        notify.error("Please calculate a rehab estimate before saving.");
        return;
      }

      // Invalidate queries to refresh data
      await queryClient.invalidateQueries({
        queryKey: ["rehab-estimate", rehabEstimateSchemaName, id],
      });

      navigate("/property-portal/rehab-estimator");
    } catch (error) {
      console.error("Save error:", error);
      notify.error("Failed to save rehab estimate.");
    }
  };

  const handleCancel = () => {
    navigate("/property-portal/rehab-estimator");
  };

  return (
    <Layout>
      <div className="max-w-5xl space-y-4 lg:space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground lg:text-3xl">
            Calculate Rehab Estimate
          </h1>
        </div>

        <form onSubmit={handleSave} className="space-y-6">
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle>Rehab Estimation Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Hidden SchemaFormRender to maintain form state */}
              <div style={{ display: "none" }}>
                <SchemaFormRender form={form} schemaJson={restrictedSchema} />
              </div>

              {/* Two Column Layout */}
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                {/* Left Column */}
                <div className="space-y-4">
                  {/* Property Name */}
                  <div className="space-y-2">
                    <Label htmlFor="propertyName">Property Name:</Label>
                    <Input
                      id="propertyName"
                      value={form.values.propertyname ?? ""}
                      disabled
                      readOnly
                      placeholder="Enter property name"
                      className="bg-muted"
                    />
                  </div>

                  {/* Rehab Type */}
                  <div className="space-y-2">
                    <Label htmlFor="rehabType">Rehab Type:</Label>
                    <Select value={rehabType} onValueChange={setRehabType}>
                      <SelectTrigger id="rehabType">
                        <SelectValue placeholder="Select rehab type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="EXCELLENT">EXCELLENT</SelectItem>
                        <SelectItem value="GOOD">GOOD</SelectItem>
                        <SelectItem value="OK">OK</SelectItem>
                        <SelectItem value="ROUGH">ROUGH</SelectItem>
                        <SelectItem value="VACANT ROUGH">
                          VACANT ROUGH
                        </SelectItem>
                        <SelectItem value="BAD">BAD</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Rehab Estimate */}
                  <div className="space-y-2">
                    <Label htmlFor="rehabEstimate">Rehab Estimate:</Label>
                    <Input
                      id="rehabEstimate"
                      value={rehabEstimateData.rehabestimate ?? ""}
                      disabled
                      placeholder="Calculated automatically"
                      className="bg-muted"
                    />
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-4">
                  {/* Sq Footage */}
                  <div className="space-y-2">
                    <Label htmlFor="sqFootage">Sq Footage:</Label>
                    <Input
                      id="sqFootage"
                      type="number"
                      value={form.values.sqfootage ?? ""}
                      disabled
                      readOnly
                      placeholder="Enter sq footage"
                      className="bg-muted"
                    />
                  </div>

                  {/* Rehab Value */}
                  <div className="space-y-2">
                    <Label htmlFor="rehabValue">Rehab Value:</Label>
                    <Input
                      id="rehabValue"
                      value={rehabValue}
                      disabled
                      placeholder="Select rehab type to see value"
                      className="bg-muted"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex gap-4">
            <Button
              type="submit"
              className="bg-[#4a5273] text-white hover:bg-[#4a5273]/90"
            >
              Save
            </Button>
            <Button type="button" variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default PP_EditRehabEstimator;
